import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Group } from "@/lib/types";
import { CreateGroupPost } from "@/components/groups/CreateGroupPost";
import { GroupPosts } from "@/components/groups/GroupPosts";
import { GroupMembers } from "@/components/groups/GroupMembers";
import { GroupSettings } from "@/components/groups/GroupSettings";
import { Users, MessageSquare, Settings, ArrowLeft, Plus } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const GroupDetail = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [group, setGroup] = useState<Group | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMember, setIsMember] = useState(false);
  const [userRole, setUserRole] = useState<string>("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showCreatePost, setShowCreatePost] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
        return;
      }
      setIsAuthenticated(true);
      fetchGroupDetails();
    };

    checkAuth();
  }, [groupId, navigate]);

  const fetchGroupDetails = async () => {
    if (!groupId) return;
    
    try {
      setIsLoading(true);
      
      // Fetch group details
      const { data: groupData, error: groupError } = await supabase
        .from('groups')
        .select(`
          *,
          profiles:created_by (
            username,
            avatar_url
          )
        `)
        .eq('id', groupId)
        .single();

      if (groupError) throw groupError;
      setGroup(groupData);

      // Check if user is a member
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: memberData } = await supabase
          .from('group_members')
          .select('*')
          .eq('group_id', groupId)
          .eq('user_id', user.id)
          .maybeSingle();

        setIsMember(!!memberData);

        // Check user role
        const { data: roleData } = await supabase
          .from('group_roles')
          .select('role')
          .eq('group_id', groupId)
          .eq('user_id', user.id)
          .maybeSingle();

        setUserRole(roleData?.role || '');
      }
    } catch (error: any) {
      console.error('Error fetching group details:', error);
      toast({
        title: "Error",
        description: "Failed to load group details",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleJoinGroup = async () => {
    if (!groupId || !isAuthenticated) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('group_members')
        .insert({ 
          group_id: groupId, 
          user_id: user.id 
        });

      if (error) throw error;

      // Update member count
      if (group) {
        const { error: updateError } = await supabase
          .from('groups')
          .update({ members_count: (group.members_count || 0) + 1 })
          .eq('id', groupId);

        if (updateError) throw updateError;
      }

      setIsMember(true);
      toast({
        title: "Joined group! 🎉",
        description: "Welcome to the community!",
      });
      
      // Refresh group details
      fetchGroupDetails();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handlePostCreated = () => {
    setShowCreatePost(false);
    // Refresh posts will be handled by GroupPosts component
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-primary/10 rounded-full flex items-center justify-center animate-pulse">
              <Users className="w-8 h-8 text-primary" />
            </div>
            <p className="text-lg font-medium">Loading group...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!group) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <p className="text-lg font-medium mb-4">Group not found</p>
            <Button onClick={() => navigate('/groups')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Groups
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-purple-900/20">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto">
            {/* Group Header */}
            <Card className="mb-8 bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <Button 
                    variant="ghost" 
                    onClick={() => navigate('/groups')}
                    className="text-white hover:bg-white/20 p-2"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </Button>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-white/20 text-white border-0">
                      {group.category}
                    </Badge>
                    <Badge variant="secondary" className="bg-white/20 text-white border-0">
                      <Users className="w-3 h-3 mr-1" />
                      {group.members_count || 0} members
                    </Badge>
                  </div>
                </div>
                
                <div className="mt-4">
                  <h1 className="text-3xl font-bold mb-2">{group.name}</h1>
                  <p className="text-blue-100 text-lg mb-4">{group.description}</p>
                  
                  {group.topics && group.topics.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {group.topics.map((topic, index) => (
                        <Badge key={index} variant="outline" className="text-white border-white/30">
                          {topic}
                        </Badge>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-4">
                    {!isMember && (
                      <Button 
                        onClick={handleJoinGroup}
                        className="bg-white text-purple-600 hover:bg-blue-50"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Join Group
                      </Button>
                    )}
                    
                    {isMember && (
                      <Button 
                        onClick={() => setShowCreatePost(!showCreatePost)}
                        className="bg-white text-purple-600 hover:bg-blue-50"
                      >
                        <MessageSquare className="w-4 h-4 mr-2" />
                        {showCreatePost ? 'Cancel' : 'Create Post'}
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Create Post Form */}
            {isMember && showCreatePost && (
              <Card className="mb-8">
                <CardContent className="p-6">
                  <CreateGroupPost 
                    groupId={groupId!} 
                    onPostCreated={handlePostCreated}
                  />
                </CardContent>
              </Card>
            )}

            {/* Content Tabs */}
            <Tabs defaultValue="posts" className="space-y-6">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="posts" className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Posts
                </TabsTrigger>
                <TabsTrigger value="members" className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Members
                </TabsTrigger>
                {userRole === 'admin' && (
                  <TabsTrigger value="settings" className="flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    Settings
                  </TabsTrigger>
                )}
              </TabsList>

              <TabsContent value="posts">
                <GroupPosts groupId={groupId!} isAdmin={userRole === 'admin'} />
              </TabsContent>
              
              <TabsContent value="members">
                <GroupMembers groupId={groupId!} isAdmin={userRole === 'admin'} />
              </TabsContent>
              
              {userRole === 'admin' && (
                <TabsContent value="settings">
                  <GroupSettings groupId={groupId!} isAdmin={userRole === 'admin'} />
                </TabsContent>
              )}
            </Tabs>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default GroupDetail;